import { useState } from "react";
import {
  Box,
  FormControlLabel,
  MenuItem,
  TextField as MuiTextField,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { match } from "ts-pattern";
import { ValidationError } from "yup";

import { dayjs } from "@thalamos/common";
import type {
  CorrectS132AttemptRequest,
  MhaStatus,
  RecordS132AttemptRequest,
  S132Attempt,
  S132Record,
} from "@thalamos/aspire-common";
import {
  correctS132AttemptSchema,
  recordS132AttemptSchema,
  S132_DUTY_FOR,
  S132Outcome,
} from "@thalamos/aspire-common";

import { s132DutyProvision } from "./S132StatusPill.js";
import {
  Button,
  HelperText,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";
import { ukLocalFormatDateTime } from "~/util.js";

export type S132SectionChoice = {
  section: MhaStatus;
  label: string;
};

export type RecordS132AttemptDialogProps = {
  patientName?: string;
  recorderName?: string;
  /** Sections the attempt may be for, most relevant first; a single one is shown fixed. */
  sections: S132SectionChoice[];
  defaultSection?: MhaStatus;
  /** Set to correct an existing attempt rather than record a new one. */
  correcting?: S132Attempt;
  /** Shown above the form when something other than the user opened the dialog. */
  intro?: string;
  cancelLabel?: string;
  onClose: () => void;
  /** Saves the attempt, or the correction when `correcting` is set. */
  onSave: (attempt: S132AttemptSubmission) => Promise<S132Record>;
  onSaved: (record: S132Record) => void;
};

export type S132AttemptSubmission = {
  request: RecordS132AttemptRequest;
  correction?: { attemptId: string; correctionReason: string };
};

const LOCAL_INPUT_FORMAT = "YYYY-MM-DDTHH:mm";

const hint = { fontSize: "0.75rem", color: "#7B7D89", mt: 0.5 };
const labelSx = ({ invalid }: { invalid: boolean }) => ({
  fontWeight: 600,
  fontSize: "0.85rem",
  mb: 0.75,
  color: invalid ? "error.main" : undefined,
});

type AttemptField = keyof CorrectS132AttemptRequest;
type FieldErrors = Partial<Record<AttemptField, string>>;

const isAttemptField = (path: string | undefined): path is AttemptField =>
  path !== undefined && path in correctS132AttemptSchema.fields;

/** The first message for each invalid field, so every one is highlighted at once. */
function fieldErrorsFrom(error: ValidationError): FieldErrors {
  const errors: FieldErrors = {};
  for (const { path, message } of error.inner) {
    if (isAttemptField(path)) errors[path] ??= message;
  }
  return errors;
}

function FieldErrorText(props: { message?: string }) {
  return props.message ? <HelperText errorMessage={props.message} /> : null;
}

// The question is worded as capacity, but its answer is still the attempt's
// S132Outcome: Yes records Understood.
const capacityAnswerText = (outcome: S132Outcome): string =>
  match(outcome)
    .with(S132Outcome.Understood, () => "Yes")
    .with(S132Outcome.NotUnderstood, () => "No")
    .with(S132Outcome.UnableToAssess, () => "Not able to assess")
    .exhaustive();

/** Records one attempt, or a correction that supersedes one; the "Edit" the user sees changes nothing in place. */
export function RecordS132AttemptDialog(props: RecordS132AttemptDialogProps) {
  const original = props.correcting;
  const onlySection =
    props.sections.length === 1 ? props.sections[0]?.section : undefined;

  const [section, setSection] = useState<MhaStatus | undefined>(
    original?.section ?? props.defaultSection ?? onlySection,
  );
  const [occurredAt, setOccurredAt] = useState(
    dayjs(original?.occurredAt).format(LOCAL_INPUT_FORMAT),
  );
  const [deliveredBySomeoneElse, setDeliveredBySomeoneElse] = useState(
    !!original?.deliveredByName,
  );
  const [deliveredByName, setDeliveredByName] = useState(
    original?.deliveredByName ?? "",
  );
  const [outcome, setOutcome] = useState<S132Outcome | undefined>(
    original?.outcome,
  );
  const [reason, setReason] = useState(original?.reason ?? "");
  const [writtenInformationGiven, setWrittenInformationGiven] = useState<
    boolean | undefined
  >(original?.writtenInformationGiven);
  const [notes, setNotes] = useState(original?.notes ?? "");
  const [correctionReason, setCorrectionReason] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const needsReason = !!outcome && outcome !== S132Outcome.Understood;
  const sectionFixed = onlySection !== undefined;
  const duty = section === undefined ? undefined : S132_DUTY_FOR[section];
  const title = original
    ? "Edit an attempt"
    : `Record ${s132DutyProvision(duty ?? "s132")} information`;

  const clearFieldError = (field: AttemptField) => {
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const save = async () => {
    setError(null);
    const parsedOccurredAt = dayjs(occurredAt, LOCAL_INPUT_FORMAT);
    const body: RecordS132AttemptRequest = {
      section: section as MhaStatus,
      // An unparseable value is passed on as entered so the schema reports it.
      occurredAt: parsedOccurredAt.isValid()
        ? parsedOccurredAt.toISOString()
        : occurredAt,
      outcome: outcome as S132Outcome,
      reason: needsReason ? reason : undefined,
      writtenInformationGiven: writtenInformationGiven as boolean,
      deliveredByName: deliveredBySomeoneElse ? deliveredByName : undefined,
      notes: notes || undefined,
    };

    let errors: FieldErrors = {};
    try {
      await (original
        ? correctS132AttemptSchema.validate(
            { ...body, correctionReason },
            { abortEarly: false },
          )
        : recordS132AttemptSchema.validate(body, { abortEarly: false }));
    } catch (e) {
      if (!(e instanceof ValidationError)) {
        setError("Something went wrong. Try again.");
        return;
      }
      errors = fieldErrorsFrom(e);
      if (Object.keys(errors).length === 0) {
        setError(e.errors[0] ?? e.message);
        return;
      }
    }
    // A blank name is recorded as given by the recorder, so "Someone else"
    // needs a name.
    if (deliveredBySomeoneElse && !deliveredByName.trim()) {
      errors.deliveredByName =
        "Enter the name and role of whoever provided the information";
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSaving(true);
    try {
      const saved = await props.onSave({
        request: body,
        correction: original
          ? { attemptId: original.id, correctionReason }
          : undefined,
      });
      props.onSaved(saved);
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? e.message
          : "The attempt could not be saved. Try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PopupDialog open onClose={props.onClose} maxWidth="sm">
      <PopupDialogTitle closeDialog={props.onClose} titleText={title} />
      <Typography sx={{ fontSize: "0.85rem", color: "#424552", mb: 2 }}>
        {props.patientName}
      </Typography>

      <Box sx={{ display: "grid", gap: 2.5 }}>
        {props.intro && (
          <Typography
            sx={{
              backgroundColor: "#EDF3FC",
              borderRadius: "4px",
              padding: "10px 12px",
              fontSize: "0.85rem",
            }}
          >
            {props.intro}
          </Typography>
        )}

        {original && (
          <Box
            sx={{
              backgroundColor: "#FFF3CA",
              border: "1px solid #E1B205",
              borderRadius: "4px",
              padding: "8px 12px",
              fontSize: "0.85rem",
              color: "#6B4E00",
            }}
          >
            {`You're amending the attempt recorded by ${original.recordedByName ?? "another user"} on ${ukLocalFormatDateTime(original.recordedAt)}. The original stays in the record, marked as amended.`}
          </Box>
        )}

        <Box>
          <MuiTextField
            select
            fullWidth
            size="small"
            name="section"
            label="Which section did you explain?"
            value={section ?? ""}
            disabled={sectionFixed}
            error={!!fieldErrors.section}
            onChange={(e) => {
              setSection(e.target.value as MhaStatus);
              clearFieldError("section");
            }}
          >
            {props.sections.map((choice) => (
              <MenuItem key={choice.section} value={choice.section}>
                {choice.label}
              </MenuItem>
            ))}
          </MuiTextField>
          <FieldErrorText message={fieldErrors.section} />
        </Box>

        <Box sx={{ maxWidth: "20rem" }}>
          <Typography sx={labelSx({ invalid: !!fieldErrors.occurredAt })}>
            When was the patient read their rights?
          </Typography>
          <MuiTextField
            fullWidth
            size="small"
            type="datetime-local"
            name="occurredAt"
            value={occurredAt}
            error={!!fieldErrors.occurredAt}
            slotProps={{
              htmlInput: {
                max: dayjs().format(LOCAL_INPUT_FORMAT),
                "aria-label": "When was the patient read their rights?",
              },
            }}
            onChange={(e) => {
              setOccurredAt(e.target.value);
              clearFieldError("occurredAt");
            }}
          />
          <FieldErrorText message={fieldErrors.occurredAt} />
          <Typography sx={hint}>Can be in the past</Typography>
        </Box>

        <Box>
          <Typography
            sx={labelSx({ invalid: !!fieldErrors.deliveredByName })}
            id="s132-delivered-by-label"
          >
            Who provided the information?
          </Typography>
          <RadioGroup
            row
            aria-labelledby="s132-delivered-by-label"
            value={deliveredBySomeoneElse ? "someoneElse" : "me"}
            onChange={(e) => {
              setDeliveredBySomeoneElse(e.target.value === "someoneElse");
              clearFieldError("deliveredByName");
            }}
          >
            <FormControlLabel
              value="me"
              control={<Radio size="small" />}
              label="I did"
            />
            <FormControlLabel
              value="someoneElse"
              control={<Radio size="small" />}
              label="Someone else"
            />
          </RadioGroup>
          {deliveredBySomeoneElse && (
            <MuiTextField
              fullWidth
              size="small"
              name="deliveredByName"
              label="Their name and role"
              placeholder="e.g. Chris Hale, bank RMN"
              value={deliveredByName}
              error={!!fieldErrors.deliveredByName}
              onChange={(e) => {
                setDeliveredByName(e.target.value);
                clearFieldError("deliveredByName");
              }}
              sx={{ mt: 1 }}
            />
          )}
          <FieldErrorText message={fieldErrors.deliveredByName} />
        </Box>

        <Box>
          <Typography
            sx={labelSx({ invalid: !!fieldErrors.outcome })}
            id="s132-outcome-label"
          >
            Does the individual have the capacity to understand the information?
          </Typography>
          <MuiTextField
            select
            size="small"
            name="outcome"
            value={outcome ?? ""}
            error={!!fieldErrors.outcome}
            slotProps={{ select: { labelId: "s132-outcome-label" } }}
            onChange={(e) => {
              setOutcome(
                Object.values(S132Outcome).find(
                  (value) => String(value) === e.target.value,
                ),
              );
              clearFieldError("outcome");
            }}
            sx={{ width: "20rem", maxWidth: "100%" }}
          >
            {Object.values(S132Outcome).map((value) => (
              <MenuItem key={value} value={value}>
                {capacityAnswerText(value)}
              </MenuItem>
            ))}
          </MuiTextField>
          <FieldErrorText message={fieldErrors.outcome} />
        </Box>

        {needsReason && (
          <Box>
            <MuiTextField
              fullWidth
              multiline
              minRows={2}
              name="reason"
              label="Please provide more information"
              value={reason}
              error={!!fieldErrors.reason}
              onChange={(e) => {
                setReason(e.target.value);
                clearFieldError("reason");
              }}
            />
            <FieldErrorText message={fieldErrors.reason} />
          </Box>
        )}

        <Box>
          <Typography
            sx={labelSx({ invalid: !!fieldErrors.writtenInformationGiven })}
            id="s132-written-information-label"
          >
            Did you provide written information?
          </Typography>
          <RadioGroup
            row
            aria-labelledby="s132-written-information-label"
            value={
              writtenInformationGiven === undefined
                ? ""
                : String(writtenInformationGiven)
            }
            onChange={(e) => {
              setWrittenInformationGiven(e.target.value === "true");
              clearFieldError("writtenInformationGiven");
            }}
          >
            <FormControlLabel
              value="true"
              control={<Radio size="small" />}
              label="Yes"
            />
            <FormControlLabel
              value="false"
              control={<Radio size="small" />}
              label="No"
            />
          </RadioGroup>
          <FieldErrorText message={fieldErrors.writtenInformationGiven} />
        </Box>

        <Box>
          <MuiTextField
            fullWidth
            multiline
            minRows={2}
            name="notes"
            label="Notes (optional)"
            placeholder="How the information was given, e.g. interpreter, easy-read leaflet"
            value={notes}
            error={!!fieldErrors.notes}
            onChange={(e) => {
              setNotes(e.target.value);
              clearFieldError("notes");
            }}
          />
          <FieldErrorText message={fieldErrors.notes} />
        </Box>

        {original && (
          <Box>
            <MuiTextField
              fullWidth
              multiline
              minRows={2}
              name="correctionReason"
              label="Reason for amendment"
              value={correctionReason}
              error={!!fieldErrors.correctionReason}
              onChange={(e) => {
                setCorrectionReason(e.target.value);
                clearFieldError("correctionReason");
              }}
            />
            <FieldErrorText message={fieldErrors.correctionReason} />
            <Typography sx={hint}>
              Required. Shown alongside the amendment in the history.
            </Typography>
          </Box>
        )}

        <Typography
          sx={{
            fontSize: "0.75rem",
            color: "#7B7D89",
            backgroundColor: "#F8F8F9",
            borderRadius: "4px",
            padding: "8px 10px",
          }}
        >
          {`Recorded as ${props.recorderName ?? "you"}, at the time you save.`}
        </Typography>

        {error && (
          <Typography
            role="alert"
            sx={{ color: "#C74418", fontSize: "0.85rem" }}
          >
            {error}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button
          label={props.cancelLabel ?? "Cancel"}
          outlined
          onClick={props.onClose}
        />
        <Button
          label="Save"
          disabled={isSaving}
          onClick={() => {
            void save();
          }}
        />
      </Box>
    </PopupDialog>
  );
}
